// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { Layout, Button, Badge } from 'antd';
import { UserOutput, BrandingParams, PermissionsEnum } from 'thehive-sdk';
import AccountMenu from '@Organisms/MainLayout/AccountMenu/AccountMenu';
import { useCurrentUser } from '@Queries/userQueries';
import { Link, useHistory } from 'react-router-dom';
import { getAvatarUrl, classList, handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { ADMIN_ORG_ID } from '@Types';
import RouteBuilder from '@Services/RouteBuilder';
import GlobalFetchingIndicator from '@Molecules/GlobalFetchingIndicator/GlobalFetchingIndicator';
import BreadcrumbManager from '@Molecules/BreadcrumbManager/BreadcrumbManager';
import client from '@Client';
import { KnowledgeBaseView } from '@Organisms/KnowledgeBase/KnowledgeBaseView/KnowledgeBaseView';
import { computeEventList } from '@Organisms/Case/eventList';
import { CaseCreateManage } from '@Organisms/Case/CaseCreate/CaseCreateManage';
import { CaseSearch } from '@Molecules/Case';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import LiveFeed from '../LiveFeed/LiveFeed';
import { matchPath } from 'react-router-dom';
import { LanguageSwitcher } from '@Atoms/LanguageSwitcher/LanguageSwitcher';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ICONS } from '@Constants/iconsApp';
import { StatusContext } from '@Context/StatusProvider';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { baseUrl } from '../../../../index';
import { useStorageOptions } from '@Hooks/useStorageOptions';

import './ThHeader.scss';

const { Header } = Layout;

const TitleLivestream = ({ counter, onClick }: { counter: number; onClick?: () => void }) => {
    const local = useLocalMessage();
    // remove AWS added path
    let urlWithoutBase = document.location.pathname;
    if (urlWithoutBase.includes(baseUrl)) {
        urlWithoutBase = urlWithoutBase.replace(baseUrl, '/');
    }

    const { caseId = '' } =
        matchPath<{ caseId: string }>(urlWithoutBase, {
            path: RouteBuilder.compute('cases.layout'),
            exact: false,
            strict: false,
        })?.params || {};

    const onClickHandler = useCallback(() => {
        onClick?.();
        const base = (document.getElementsByTagName('base')[0] || '').href.replace(/\/$/, '');
        const url = caseId ? RouteBuilder.compute('live.details', { caseId }) : RouteBuilder.compute('live.default');

        window.open(
            base + url,
            'TheHiveLive',
            'width=500,height=700,menubar=no,status=no,toolbar=no,location=no,scrollbars=yes',
        );
    }, [caseId]); // eslint-disable-line

    return (
        <>
            <span className="header-title__text">
                ({counter}) {local('mainLayout.liveFeed.title')}
            </span>
            <Button
                type="link"
                icon={<ICONS.ArrowsAltOutlined />}
                onClick={onClickHandler}
                className="live-feed-header__icon"
            />
        </>
    );
};

interface ThHeaderProps {
    userData?: UserOutput;
    brandingData?: BrandingParams;
}

const ThHeader = ({ userData, brandingData }: ThHeaderProps): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const isAdmin = userData && userData.organisation === ADMIN_ORG_ID;
    const currentSpace = isAdmin ? 'admin' : 'user';

    const { data: currentUser } = useCurrentUser();
    const [counter, setCounter] = useState(0);
    const [visible, setVisible] = useState<boolean>(false);

    const { licenseCapabilities } = useContext(StatusContext);

    const importArchiveCapabilities = useMemo(
        () => !!licenseCapabilities?.case?.importArchive || false,
        [licenseCapabilities],
    );

    const { homepage } = useStorageOptions();

    const { organisation = '', organisations = [], permissions = [] } = currentUser || {};

    const hasPermissions = useSplitPermissions(permissions, [PermissionsEnum.MANAGE_CASE_CREATE]);

    const { onCreate, onImport, onMISPImport } = computeEventList({
        controller: client.CaseController,
        locale: i,
        onError: handleNotificationError,
    });

    const isUserAdmin = organisation === ADMIN_ORG_ID;

    return (
        <Header className="th-header">
            <div className="th-header__left-wrapper">
                <Link
                    className={classList('th-header__logo', { padded: organisations.length === 1 })}
                    to={homepage || RouteBuilder.compute('cases.default')}
                >
                    <img
                        src={
                            brandingData?.menuLogo && brandingData?.menuLogo.trim() !== ''
                                ? getAvatarUrl(brandingData.menuLogo)
                                : `static/images/logo.bee.svg`
                        }
                        alt="logo"
                    />
                </Link>

                <GlobalFetchingIndicator />

                <div className="th-header__breadcrumb-manager">
                    <BreadcrumbManager space={currentSpace} />
                </div>
            </div>

            <div className="th-header__actions-wrapper">
                {!isUserAdmin && <CaseSearch />}
                {!isUserAdmin && hasPermissions.createCase && (
                    <CaseCreateManage
                        onCaseCreate={(data) =>
                            onCreate(data)
                                .then(({ _id: caseId }) => {
                                    openMessage('success', i('notification.case.create'));
                                    history.push(RouteBuilder.compute('cases.details', { caseId }));
                                })
                                .catch(handleNotificationError)
                        }
                        disabledImport={!importArchiveCapabilities}
                        onImport={(dataForm) => {
                            return onImport(dataForm)
                                .then(({ case: { _id: caseId } }) => {
                                    openMessage('success', i('notification.case.create'));
                                    history.push(RouteBuilder.compute('cases.details', { caseId }));
                                })
                                .catch(handleNotificationError);
                        }}
                        onMispImport={(dataForm) => {
                            return onMISPImport(dataForm)
                                .then(({ _id: caseId }) => {
                                    openMessage('success', i('notification.case.create'));
                                    history.push(RouteBuilder.compute('cases.details', { caseId }));
                                })
                                .catch(handleNotificationError);
                        }}
                        triggerElem={
                            <THButton
                                type="ghost"
                                icon={<ICONS.PlusIcon className="case-create__icon" />}
                                className="case-create"
                            >
                                <span className="case-create__label">{i('app.case.create.button.label')}</span>
                            </THButton>
                        }
                    />
                )}

                <KnowledgeBaseView />

                <LanguageSwitcher />

                <AccountMenu user={userData} />

                {!isUserAdmin && (
                    <THDrawer
                        title={<TitleLivestream counter={counter} onClick={() => setVisible(false)} />}
                        className="livestream-drawer"
                        visible={visible}
                        afterVisibleChange={(visibility) => setVisible(visibility)}
                        triggerElem={
                            <Badge>
                                <THButton
                                    icon={<ICONS.ThunderboltOutlined />}
                                    size="large"
                                    color="yellow"
                                    type="primary"
                                />
                            </Badge>
                        }
                    >
                        <LiveFeed setCounter={(value) => setCounter(value)} />
                    </THDrawer>
                )}
            </div>
        </Header>
    );
};

export default ThHeader;
